<template>
    <div
        class="redesign-extension-card-body"
        :class="{
            'redesign-extension-card-body--expanded': showAllCalls
        }"
    >
        <!-- Total Calls Summary Bar -->
        <div
            class="total-calls-statistic absolute w-full grid gap-1 grid-cols-[auto,1fr] text-xs px-2 py-1.5 bg-gray-100"
            :class="{
                'total-calls-statistic--hidden': isTotalCallsHidden
            }"
        >
            <div class="text-[10px] uppercase font-medium text-steel">
                {{ totalCallsStatistic?.label }}
            </div>
            <div class="flex gap-3 pl-6">
                <div class="flex gap-1">
                    <RedesignBaseIcon
                        :name="totalCallsStatistic?.outgoing.icon"
                        :color="totalCallsStatistic?.outgoing.iconColor"
                        class="flex-shrink-0"
                    />
                    <div>{{ totalCallsStatistic?.outgoing.value }}</div>
                </div>
                <div class="flex gap-1">
                    <RedesignBaseIcon
                        :name="totalCallsStatistic?.incoming.icon"
                        :color="totalCallsStatistic?.incoming.iconColor"
                        class="flex-shrink-0"
                    />
                    <div>{{ totalCallsStatistic?.incoming.value }}</div>
                </div>
                <div class="flex gap-1">
                    <RedesignBaseIcon
                        :name="totalCallsStatistic?.other.icon"
                        :color="totalCallsStatistic?.other.iconColor"
                        class="flex-shrink-0"
                    />
                    <div>{{ totalCallsStatistic?.other.value }}</div>
                </div>
            </div>
        </div>

        <!-- Summary Panel (Statistics Grid) -->
        <div
            class="summary-container text-xs px-2 py-3"
            :class="[
                { 'summary-container--active': showSummary && showStatistics },
                { 'px-4': cardsCols <= 5 }
            ]"
        >
            <div
                v-for="(item, index) in statistics"
                :key="index"
                :class="[
                    { 'summary-row-small': cardsCols > 5 },
                    { 'summary-row-medium': cardsCols === 5 }
                ]"
                class="summary-row grid gap-1 mb-3.5 grid-cols-2"
            >
                <!-- Label -->
                <div v-if="cardsCols <= 5">
                    {{ item.label }}
                </div>
                <VcPopover
                    v-else
                    placement="top"
                    trigger="hover"
                >
                    <template #reference>
                        <div class="summary-col summary-col-label text-no-wrap">
                            {{ item.label }}
                        </div>
                    </template>
                    <div class="p-3">
                        {{ item.label }}
                    </div>
                </VcPopover>

                <!-- Values -->
                <div class="grid grid-cols-3 gap-x-1">
                    <div class="summary-col">
                        <div class="flex gap-1">
                            <RedesignBaseIcon
                                v-if="item.outgoing.icon"
                                :name="item.outgoing.icon"
                                :color="item.outgoing.iconColor"
                                class="flex-shrink-0"
                            />
                            <div>{{ item.outgoing.value }}</div>
                        </div>
                    </div>
                    <div class="summary-col">
                        <div class="flex gap-1">
                            <RedesignBaseIcon
                                v-if="item.incoming.icon"
                                :name="item.incoming.icon"
                                :color="item.incoming.iconColor"
                                class="flex-shrink-0"
                            />
                            <div>{{ item.incoming.value }}</div>
                        </div>
                    </div>
                    <div class="summary-col">
                        <div class="flex gap-1">
                            <RedesignBaseIcon
                                v-if="item.other.icon"
                                :name="item.other.icon"
                                :color="item.other.iconColor"
                                class="flex-shrink-0"
                            />
                            <div>{{ item.other.value }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Calls Section -->
        <div
            :class="{ 'pt-7': showTotalCallsSummary && totalCallsStatistic && !showAllCalls }"
            class="transition"
        >
            <div>
                <!-- Show All Toggle -->
                <div
                    v-if="shouldShowCallsToggle"
                    class="flex text-xs gap-4 px-4 pt-2 mb-1"
                >
                    <RedesignTag
                        outline
                        :tag-content="[{ text: callsCountLabel }]"
                    />
                    <RedesignToggleButton
                        bg-color="blue"
                        border-radius="18px"
                        size="xs"
                        :on-label="t('common.showAll')"
                        :off-label="t('common.showAll')"
                        type="primary"
                        :round="true"
                        :disabled="false"
                        @toggle="emit('toggleShowAllCalls', $event)"
                    />
                </div>

                <!-- Calls List -->
                <div
                    v-if="calls.length"
                    :class="{ 'overflow-y-scroll': calls.length > 1 }"
                    :style="{ height: showAllCalls ? '120px' : 'auto' }"
                >
                    <RedesignCallInfo
                        v-for="call in visibleCalls"
                        :key="`${call.ivrid}_${call.direction}`"
                        :call="call"
                        :settings="settings"
                        :cards-cols="cardsCols"
                        class="px-4 flex number-content items-center justify-between py-2"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcPopover } from '@voicenter-team/voicenter-ui-plus'
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignToggleButton from '@/components/RedesignBaseComponents/RedesignToggleButton.vue'
import RedesignCallInfo from '@/components/RedesignComponents/Cards/ExtensionCard/RedesignCallInfo.vue'
import { Settings } from '@/types/dashboard'
import { StatisticItem } from '@/composables/useExtensionCardStatistics'

interface Call {
    ivrid: string | number
    direction: string
    [key: string]: unknown
}

const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        // Display props
        cardsCols: number
        showStatistics: boolean
        showSummary: boolean
        showAllCalls: boolean
        showTotalCallsSummary: boolean
        settings: Settings
        // Data props
        statistics: StatisticItem[]
        totalCallsStatistic: StatisticItem | undefined
        calls: Call[]
    }>(),
    {
        showSummary: false,
        showAllCalls: false,
        showTotalCallsSummary: true,
        settings: () => ({})
    }
)

const emit = defineEmits<{
    (e: 'toggleShowAllCalls', value: boolean): void
}>()

// Computed
const isTotalCallsHidden = computed(() =>
    !props.showTotalCallsSummary || props.showAllCalls || !props.totalCallsStatistic
)

const visibleCalls = computed(() => {
    const condition = props.showTotalCallsSummary &&
        props.totalCallsStatistic &&
        !props.showAllCalls &&
        props.calls.length > 2
    return condition ? props.calls.slice(0, 1) : props.calls
})

const shouldShowCallsToggle = computed(() => {
    const threshold = props.showTotalCallsSummary ? 2 : 3
    return props.calls.length > threshold
})

const callsCountLabel = computed(() =>
    props.calls.length === 1 ? props.calls.length + ' call' : props.calls.length + ' calls'
)
</script>

<style scoped lang="scss">
.redesign-extension-card-body {
    min-height: 150px;
    position: relative;

    .text-no-wrap {
        text-wrap: nowrap;
        text-overflow: ellipsis;
        width: 100%;
        overflow: hidden;
    }

    .summary-row {
        @apply grid grid-cols-2;
    }

    .summary-row-medium {
        grid-template-columns: 35% 65%;
    }

    .summary-row-small {
        grid-template-columns: 23% 77%;
    }

    .total-calls-statistic {
        overflow: hidden;
        transition: max-height 0.5s ease-out, opacity 0.5s ease-out;
        max-height: 30px;
        opacity: 1;
        will-change: max-height, opacity;

        &--hidden {
            max-height: 0;
            opacity: 0;
        }
    }
}

.summary-container {
    background-color: var(--cardsBgColor-color);
    color: var(--mainTextColor-color);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    min-height: 150px;
    overflow-y: auto;
    z-index: -1;
    pointer-events: none;
    transform: translateY(100%);
    transition: transform 0.5s ease, z-index 0s 0.5s;
}

.summary-container--active {
    z-index: 500;
    pointer-events: auto;
    transform: translateY(0);
    transition: transform 0.5s ease, z-index 0s 0s;
}
</style>
